import React, { useEffect, useRef, useState } from "react";
import Input from "../../components/Input";
import Select from "../../components/Select";
import { useSnackbar } from "../../components/Snackbar";
import useUpdateMerchantConfigMutation from "../../hooks/data/mutations/useUpdateMerchantConfigMutation";
import { AccountHolder } from "../../services/accountService";
import merchantStatus from "../../data/merchantStatus.json";
import ratePlanTypes from "../../data/ratePlanTypes.json";
import Panel from "./Panel";
import EditControls from "./EditControls";
import Switch from "../../components/Switch";
import { Popover as HeadlessUiPopover } from "@headlessui/react";
import Popover from "../../components/Popover";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import HasAccess, { csrRoles } from "../../components/HasAccess";
import useTimezonesQuery from "../../hooks/data/queries/useTimezonesQuery";
import Tooltip from "../../components/Tooltip";
import Button from "../../components/Button";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import type { Corporation } from "../../services/accountService";
import useCorporationsQuery from "../../hooks/data/queries/useCorporationsQuery";

type MerchantConfigData = Pick<
  AccountHolder,
  | "merchantStatus"
  | "techFeeRate"
  | "convFee"
  | "convFeeRate"
  | "timeZoneCode"
  | "useTransAmountLimit"
  | "useACHTransAmountLimit"
  | "transAmountLimit"
  | "achTransAmountLimit"
  | "useDailyTransAmountLimit"
  | "useACHDailyTransAmountLimit"
  | "dailyTransAmountLimit"
  | "achDailyTransAmountLimit"
  | "useTechFee"
  | "useConvFee"
  | "techFeeRate"
  | "inBalancePlatform"
  | "migrated"
  | "stores"
  | "useInterchangeRate"
  | "duplicateDetectionMinutes"
  | "corpAccountHolderId"
  | "corpFee"
  | "corpFeeDescription"
  | "useCorpFee"
> & { shopperStatement: string };

type Props = {
  accountHolderId: string;
  accountHolderCode: string;
  merchantConfigData: MerchantConfigData | undefined;
  useInterchangeRate: boolean;
  country: string;
  isLoading: boolean;
  isAdminOrCSR: boolean;
  useTechFee: boolean;
  useConvFee: boolean;
  isCorpEdition: boolean;
  merchantAccountId?: string; // ensure this prop is present
};

function MerchantConfigurations({
  accountHolderId,
  accountHolderCode,
  merchantConfigData,
  useInterchangeRate,
  country,
  isAdminOrCSR,
  isLoading,
  useTechFee,
  useConvFee,
  isCorpEdition,
  merchantAccountId = "",
}: Props): JSX.Element {
  const { mutate: updateMerchantConfig, isLoading: isUpdatingMerchantConfig } =
    useUpdateMerchantConfigMutation(accountHolderCode);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedMerchantConfigData, setEditedMerchantConfigData] =
    useState<MerchantConfigData | null>(null);

  const [usingTransAmountLimit, setUsingTransAmountLimit] = useState(
    Boolean(merchantConfigData?.useTransAmountLimit)
  );
  const [usingACHTransAmountLimit, setUsingACHTransAmountLimit] = useState(
    Boolean(merchantConfigData?.useACHTransAmountLimit)
  );

  const { data: timezones, isLoading: isLoadingTimezones } = useTimezonesQuery(
    country,
    { enabled: Boolean(country) }
  );

  const [usingDailyTransAmountLimit, setUsingDailyTransAmountLimit] = useState(
    Boolean(merchantConfigData?.useDailyTransAmountLimit)
  );
  const [usingACHDailyTransAmountLimit, setUsingACHDailyTransAmountLimit] =
    useState(Boolean(merchantConfigData?.useACHDailyTransAmountLimit));

  // Track whether duplicate detection is enabled (minutes > 0)
  const [usingDuplicateDetectionMinutes, setUsingDuplicateDetectionMinutes] =
    useState(true);

  // Keep local toggle in sync when fresh data arrives
  useEffect(() => {
    const v = merchantConfigData?.duplicateDetectionMinutes;
    if (v === null) {
      setUsingDuplicateDetectionMinutes(true);
    } else {
      setUsingDuplicateDetectionMinutes(Number(v ?? 0) > 0);
    }
  }, [merchantConfigData]);

  const [usingTechFee, setUsingTechFee] = useState(
    Boolean(merchantConfigData?.useTechFee)
  );

  const [usingConvFee, setUsingConvFee] = useState(
    Boolean(merchantConfigData?.useConvFee)
  );

  const [usingCorpFee, setUsingCorpFee] = useState(
    Boolean(merchantConfigData?.useCorpFee)
  );

  const [techFeeRateSwitchEl, setTechFeeRateSwitchEl] =
    useState<HTMLDivElement | null>(null);
  const [showTechFeeRateToggle, setShowTechFeeRateToggle] = useState(false);

  const [surchargeRateSwitchEl, setSurchargeRateSwitchEl] =
    useState<HTMLDivElement | null>(null);
  const [showSurchargeRateToggle, setShowSurchargeRateToggle] = useState(false);

  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  const merchantStatusRef = useRef<HTMLSelectElement>(null);
  const duplicateDetectionMinutesRef = useRef<HTMLInputElement>(null);

  const convFeeValid =
    isLoading || +editedMerchantConfigData?.convFeeRate! <= 4;

  // Custom Fee validation: when enabled, corpFee and corpAccountHolderId are required
  const customFeeRequiredValid = (() => {
    const enabled = Boolean(editedMerchantConfigData?.useCorpFee);
    if (!enabled) return true;
    const fee = (editedMerchantConfigData?.corpFee ?? "").toString().trim();
    const corpId = (editedMerchantConfigData?.corpAccountHolderId ?? "")
      .toString()
      .trim();
    return fee !== "" && corpId !== "";
  })();

  // Duplicate Detection validation: when enabled, must have a valid value
  const duplicateDetectionValid = (() => {
    if (!usingDuplicateDetectionMinutes) return true;
    const v = editedMerchantConfigData?.duplicateDetectionMinutes ?? "";
    const num = Number(v);
    if (v === "" || v === null || v === undefined) return false;
    if (!Number.isInteger(num)) return false;
    if (num < 0 || num > 1440) return false;
    return true;
  })();
  const saveButtonEnabled =
    convFeeValid &&
    customFeeRequiredValid &&
    duplicateDetectionValid &&
    (merchantConfigData?.merchantStatus !==
      editedMerchantConfigData?.merchantStatus ||
      merchantConfigData?.useInterchangeRate !==
        editedMerchantConfigData?.useInterchangeRate ||
      merchantConfigData?.shopperStatement !==
        editedMerchantConfigData?.shopperStatement ||
      merchantConfigData?.techFeeRate !==
        editedMerchantConfigData?.techFeeRate ||
      merchantConfigData?.convFeeRate !==
        editedMerchantConfigData?.convFeeRate ||
      merchantConfigData?.useTransAmountLimit !==
        editedMerchantConfigData?.useTransAmountLimit ||
      merchantConfigData?.useACHTransAmountLimit !==
        editedMerchantConfigData?.useACHTransAmountLimit ||
      merchantConfigData?.transAmountLimit !==
        editedMerchantConfigData?.transAmountLimit ||
      merchantConfigData?.achTransAmountLimit !==
        editedMerchantConfigData?.achTransAmountLimit ||
      merchantConfigData?.useDailyTransAmountLimit !==
        editedMerchantConfigData?.useDailyTransAmountLimit ||
      merchantConfigData?.useACHDailyTransAmountLimit !==
        editedMerchantConfigData?.useACHDailyTransAmountLimit ||
      merchantConfigData?.dailyTransAmountLimit !==
        editedMerchantConfigData?.dailyTransAmountLimit ||
      merchantConfigData?.achDailyTransAmountLimit !==
        editedMerchantConfigData?.achDailyTransAmountLimit ||
      merchantConfigData?.timeZoneCode !==
        editedMerchantConfigData?.timeZoneCode ||
      merchantConfigData?.useConvFee !== editedMerchantConfigData?.useConvFee ||
      merchantConfigData?.useTechFee !== editedMerchantConfigData?.useTechFee ||
      // Custom Fee fields changes should enable save
      merchantConfigData?.useCorpFee !== editedMerchantConfigData?.useCorpFee ||
      merchantConfigData?.corpFee !== editedMerchantConfigData?.corpFee ||
      merchantConfigData?.corpFeeDescription !==
        editedMerchantConfigData?.corpFeeDescription ||
      merchantConfigData?.corpAccountHolderId !==
        editedMerchantConfigData?.corpAccountHolderId ||
      merchantConfigData?.inBalancePlatform !==
        editedMerchantConfigData?.inBalancePlatform ||
      merchantConfigData?.duplicateDetectionMinutes !==
        editedMerchantConfigData?.duplicateDetectionMinutes);

  const canModifyStatus =
    merchantConfigData?.merchantStatus !== "Closed" || isAdminOrCSR;

  const countriesThatAllowInterchange = ["US", "PR", "MP", "UM", "VI"];
  const canModifyInterchange =
    isAdminOrCSR && countriesThatAllowInterchange.includes(country);

  useEffect(() => {
    if (merchantConfigData && !isUpdatingMerchantConfig && !isEditing) {
      setEditedMerchantConfigData(merchantConfigData);
    }
  }, [merchantConfigData, isUpdatingMerchantConfig, isEditing]);

  useEffect(() => {
    merchantStatusRef.current?.focus();
  }, [isEditing]);

  function handleUpdateMerchantConfig() {
    // Validate duplicate detection minutes if enabled
    if (usingDuplicateDetectionMinutes) {
      const v = editedMerchantConfigData?.duplicateDetectionMinutes ?? "";
      const num = Number(v);

      if (v === "" || v === null || v === undefined) {
        openSnackbar("Duplicate detection minutes must have a value", {
          type: "error",
          duration: 3000,
        });
        duplicateDetectionMinutesRef.current?.focus();
        return;
      }

      if (!Number.isInteger(num)) {
        openSnackbar("Duplicate detection minutes must be a whole number", {
          type: "error",
          duration: 3000,
        });
        duplicateDetectionMinutesRef.current?.focus();
        return;
      }

      if (num < 0) {
        openSnackbar("Duplicate detection minutes must be >= 0", {
          type: "error",
          duration: 3000,
        });
        duplicateDetectionMinutesRef.current?.focus();
        return;
      }

      if (num > 1440) {
        openSnackbar("Duplicate detection minutes must be <= 1440", {
          type: "error",
          duration: 3000,
        });
        duplicateDetectionMinutesRef.current?.focus();
        return;
      }
    }

    // Check if tech fee + interchange rate are both enabled (bypass feature flag)
    const hasTechFeeWithInterchange = Boolean(
      (editedMerchantConfigData?.useTechFee ?? useTechFee) &&
        editedMerchantConfigData?.useInterchangeRate
    );

    // Check if any fee is enabled with interchange rate (requires feature flag check)
    const hasFeeWithInterchangeAndFlag =
      (useConvFee ||
        useTechFee ||
        editedMerchantConfigData?.useConvFee ||
        editedMerchantConfigData?.useTechFee) &&
      editedMerchantConfigData?.useInterchangeRate &&
      process.env.REACT_APP_FF_DISPLAY_ICPP_SURCHARGE?.toLocaleLowerCase() !==
        "true";

    // Allow if tech fee + interchange are both enabled, OR if any fee + interchange are enabled and flag is off
    if (hasTechFeeWithInterchange || hasFeeWithInterchangeAndFlag) {
      openSnackbar(
        `Account Holder ${accountHolderCode} has Tech Fee on and Interchange++ does not apply.`,
        {
          type: "error",
          duration: 5000,
        }
      );
      return;
    }

    updateMerchantConfig(
      {
        accountHolderId,
        adyenCaptureDelay: 0,
        merchantStatus: editedMerchantConfigData?.merchantStatus ?? "",
        useInterchangeRate:
          editedMerchantConfigData?.useInterchangeRate ?? false,
        shopperStatement: editedMerchantConfigData?.shopperStatement ?? "",
        convFee: "F",
        convFeeRate: editedMerchantConfigData?.convFeeRate ?? "",
        techFeeRate: editedMerchantConfigData?.techFeeRate ?? "",
        timeZoneCode: editedMerchantConfigData?.timeZoneCode ?? "",
        useTransAmountLimit:
          editedMerchantConfigData?.useTransAmountLimit ?? false,
        useACHTransAmountLimit:
          editedMerchantConfigData?.useACHTransAmountLimit ?? false,
        transAmountLimit: editedMerchantConfigData?.transAmountLimit ?? "",
        achTransAmountLimit:
          editedMerchantConfigData?.achTransAmountLimit ?? "",
        useDailyTransAmountLimit:
          editedMerchantConfigData?.useDailyTransAmountLimit ?? false,
        useACHDailyTransAmountLimit:
          editedMerchantConfigData?.useACHDailyTransAmountLimit ?? false,
        dailyTransAmountLimit:
          editedMerchantConfigData?.dailyTransAmountLimit ?? "",
        achDailyTransAmountLimit:
          editedMerchantConfigData?.achDailyTransAmountLimit ?? "",
        useConvFee: editedMerchantConfigData?.useConvFee ?? false,
        useTechFee: editedMerchantConfigData?.useTechFee ?? false,
        inBalancePlatform: editedMerchantConfigData?.inBalancePlatform ?? false,
        duplicateDetectionMinutes:
          editedMerchantConfigData?.duplicateDetectionMinutes ?? "",
        corpId: editedMerchantConfigData?.corpAccountHolderId ?? "",
        useCorpFee: editedMerchantConfigData?.useCorpFee ?? false,
        corpFeePercentage: editedMerchantConfigData?.corpFee ?? "",
        corpDescription: editedMerchantConfigData?.corpFeeDescription ?? "",
      },
      {
        onSuccess() {
          openSnackbar("Merchant config was successfully updated.", {
            type: "success",
            duration: 3000,
          });
          setIsEditing(false);
        },
        onError() {
          openSnackbar("Failed to update merchant config. Please try again.", {
            type: "error",
            duration: 3000,
          });
        },
      }
    );
  }

  function handleEditableFieldChange(field: string, value: any) {
    setEditedMerchantConfigData({
      ...editedMerchantConfigData!,
      [field]: value,
    });
  }

  function handleUseDuplicationDetectionMinutesChange() {
    setUsingDuplicateDetectionMinutes((p) => {
      if (p === true) {
        // If turning off, also clear the minutes value
        handleEditableFieldChange("duplicateDetectionMinutes", "0");
      }
      return !p;
    });
  }

  // TODO: When REACT_APP_FF_DISPLAY_ICPP_SURCHARGE FF is removed, change the below line
  const convFeeSwitchDisabled =
    !isEditing ||
    (useInterchangeRate &&
      process.env.REACT_APP_FF_DISPLAY_ICPP_SURCHARGE?.toLocaleLowerCase() !==
        "true");

  const techFeeRateSwitchDisabled =
    !isEditing ||
    !!(editedMerchantConfigData
      ? editedMerchantConfigData.useInterchangeRate
      : useInterchangeRate);

  // Local state for Custom Fee Rate UI
  const [useCustomFeeRate, setUseCustomFeeRate] = useState<boolean>(false);
  const [customFeeDescription, setCustomFeeDescription] = useState<string>("");
  const [customFeeCorpId, setCustomFeeCorpId] = useState<string>("");

  // Load corporations filtered by current merchant account
  const { data: corporations, isLoading: isLoadingCorporations } =
    useCorporationsQuery(merchantAccountId);

  const corpOptions =
    (corporations ?? []).map((c: Corporation) => ({
      value: c.accountHolderId, // key
      label: c.corpName, // display text
    })) ?? [];

  // Feature flag: Custom Fee section visibility
  const showCustomFeeSection =
    process.env.REACT_APP_FF_CONFIGURE_CUSTOMFEE?.toLocaleLowerCase() ===
    "true";

  return (
    <Panel className="col-span-4">
      <Panel.Header title="Merchant Configurations">
        <EditControls
          customId="editMerchantConfigurationsButton"
          isEditing={isEditing}
          isSaving={isUpdatingMerchantConfig}
          editButtonProps={{
            disabled: isLoading,
            onClick: () => setIsEditing(true),
          }}
          saveButtonProps={{
            onClick: () => handleUpdateMerchantConfig(),
            disabled: !saveButtonEnabled,
          }}
          cancelButtonProps={{
            onClick: () => {
              setIsEditing(false);
              setEditedMerchantConfigData(merchantConfigData ?? null);
            },
          }}
        />
      </Panel.Header>

      <Panel.Content>
        <div className="grid grid-cols-[180px_1fr] grid-rows-[1fr_1fr_1fr_1fr_auto] gap-x-4 gap-y-8 pt-4">
          <span className="font-medium">Merchant Status</span>
          <Select
            ref={merchantStatusRef}
            id="merchantStatus"
            options={merchantStatus}
            value={editedMerchantConfigData?.merchantStatus ?? ""}
            onChange={(e) =>
              handleEditableFieldChange("merchantStatus", e.target.value)
            }
            disabled={!isEditing || !canModifyStatus}
          />
          {!isCorpEdition && (
            <>
              <div className="flex flex-row items-center gap-x-1 font-medium">
                Rate Plan
                {isAdminOrCSR && (
                  <Tooltip
                    text="Add Rate Plan Effective Dates in Admin"
                    position="top"
                  >
                    <InformationCircleIcon className="h-6 w-6 text-primary-700" />
                  </Tooltip>
                )}
              </div>
              <Select
                id="ratePlanType"
                options={ratePlanTypes}
                value={
                  editedMerchantConfigData?.useInterchangeRate
                    ? "true"
                    : "false"
                }
                onChange={(e) =>
                  handleEditableFieldChange(
                    "useInterchangeRate",
                    e.target.value === "true"
                  )
                }
                disabled={!isEditing || !canModifyInterchange}
              />
              <span className="font-medium">Shopper Statement</span>
              <Input
                id="shopperStatement"
                disabled={!isEditing || merchantConfigData?.stores.length === 0}
                value={editedMerchantConfigData?.shopperStatement ?? ""}
                onChange={(e) =>
                  handleEditableFieldChange("shopperStatement", e.target.value)
                }
                maxLength={22}
              />
              <HasAccess allowedRoles={[...csrRoles]}>
                <>
                  <span className="pt-1 font-medium">
                    ACH Transaction Limit
                  </span>
                  <div className="flex w-full items-center justify-between gap-x-2">
                    <Switch
                      checked={Boolean(
                        editedMerchantConfigData?.useACHTransAmountLimit
                      )}
                      onChange={() => {
                        setUsingACHTransAmountLimit((p) => !p);
                        handleEditableFieldChange(
                          "useACHTransAmountLimit",
                          !usingACHTransAmountLimit
                        );
                      }}
                      disabled={!isEditing}
                      label=""
                      labelProps={{ className: "font-medium w-100 mr-0" }}
                    />
                    <HeadlessUiPopover className="relative">
                      <Tippy
                        className="mb-3"
                        placement="right"
                        content="Zero means no limit"
                      >
                        <Input
                          id="achTransAmountLimit"
                          type="string"
                          label="Amount"
                          containerProps={{
                            className: "w-[85px]",
                          }}
                          disabled={!isEditing}
                          value={
                            editedMerchantConfigData?.achTransAmountLimit ?? ""
                          }
                          onChange={(e) => {
                            if (
                              e.target.value &&
                              parseFloat(e.target.value) < 0
                            ) {
                              openSnackbar(
                                "ACH Transaction Amount Limit must be >= 0",
                                {
                                  type: "error",
                                  duration: 3000,
                                }
                              );
                            } else
                              handleEditableFieldChange(
                                "achTransAmountLimit",
                                e.target.value
                              );
                          }}
                        />
                      </Tippy>
                    </HeadlessUiPopover>
                  </div>

                  <span className="pt-1 font-medium">
                    ACH Daily Volume Limit
                  </span>
                  <div className="flex w-full items-center justify-between gap-x-2">
                    <Switch
                      checked={Boolean(
                        editedMerchantConfigData?.useACHDailyTransAmountLimit
                      )}
                      onChange={() => {
                        setUsingACHDailyTransAmountLimit((p) => !p);
                        handleEditableFieldChange(
                          "useACHDailyTransAmountLimit",
                          !usingACHDailyTransAmountLimit
                        );
                      }}
                      disabled={!isEditing}
                      label=""
                      labelProps={{ className: "font-medium w-100 mr-0" }}
                    />
                    <HeadlessUiPopover className="relative">
                      <Tippy
                        className="mb-3"
                        placement="right"
                        content="Zero means no limit"
                      >
                        <Input
                          id="achDailyTransAmountLimit"
                          type="string"
                          label="Amount"
                          containerProps={{
                            className: "w-[85px]",
                          }}
                          disabled={!isEditing}
                          value={
                            editedMerchantConfigData?.achDailyTransAmountLimit ??
                            ""
                          }
                          onChange={(e) => {
                            if (
                              e.target.value &&
                              parseFloat(e.target.value) < 0
                            ) {
                              openSnackbar(
                                "ACH Daily Processing Limit must be >= 0",
                                {
                                  type: "error",
                                  duration: 3000,
                                }
                              );
                            } else
                              handleEditableFieldChange(
                                "achDailyTransAmountLimit",
                                e.target.value
                              );
                          }}
                        />
                      </Tippy>
                    </HeadlessUiPopover>
                  </div>

                  <span className="pt-1 font-medium">
                    Card Transaction Limit
                  </span>
                  <div className="flex w-full items-center justify-between gap-x-2">
                    <Switch
                      checked={Boolean(
                        editedMerchantConfigData?.useTransAmountLimit
                      )}
                      onChange={() => {
                        setUsingTransAmountLimit((p) => !p);
                        handleEditableFieldChange(
                          "useTransAmountLimit",
                          !usingTransAmountLimit
                        );
                      }}
                      disabled={!isEditing}
                      label=""
                      labelProps={{ className: "font-medium w-100 mr-0" }}
                    />
                    <HeadlessUiPopover className="relative">
                      <Tippy
                        className="mb-3"
                        placement="right"
                        content="Zero means no limit"
                      >
                        <Input
                          id="transAmountLimit"
                          type="string"
                          label="Amount"
                          containerProps={{
                            className: "w-[85px]",
                          }}
                          disabled={!isEditing}
                          value={
                            editedMerchantConfigData?.transAmountLimit ?? ""
                          }
                          onChange={(e) => {
                            if (
                              e.target.value &&
                              parseFloat(e.target.value) < 0
                            ) {
                              openSnackbar(
                                "Transaction Amount Limit must be >= 0",
                                {
                                  type: "error",
                                  duration: 3000,
                                }
                              );
                            } else
                              handleEditableFieldChange(
                                "transAmountLimit",
                                e.target.value
                              );
                          }}
                        />
                      </Tippy>
                    </HeadlessUiPopover>
                  </div>

                  <span className="pt-1 font-medium">
                    Card Daily Volume Limit
                  </span>
                  <div className="flex w-full items-center justify-between gap-x-2">
                    <Switch
                      checked={Boolean(
                        editedMerchantConfigData?.useDailyTransAmountLimit
                      )}
                      onChange={() => {
                        setUsingDailyTransAmountLimit((p) => !p);
                        handleEditableFieldChange(
                          "useDailyTransAmountLimit",
                          !usingDailyTransAmountLimit
                        );
                      }}
                      disabled={!isEditing || useInterchangeRate}
                      label=""
                      labelProps={{ className: "font-medium w-100 mr-0" }}
                    />
                    <HeadlessUiPopover className="relative">
                      <Tippy
                        className="mb-3"
                        placement="right"
                        content="Zero means no limit"
                      >
                        <Input
                          id="dailyTransAmountLimit"
                          type="string"
                          label="Amount"
                          containerProps={{
                            className: "w-[85px]",
                          }}
                          disabled={!isEditing}
                          value={
                            editedMerchantConfigData?.dailyTransAmountLimit ??
                            ""
                          }
                          onChange={(e) => {
                            if (
                              e.target.value &&
                              parseFloat(e.target.value) < 0
                            ) {
                              openSnackbar(
                                "Daily Processing Limit must be >= 0",
                                {
                                  type: "error",
                                  duration: 3000,
                                }
                              );
                            } else
                              handleEditableFieldChange(
                                "dailyTransAmountLimit",
                                e.target.value
                              );
                          }}
                        />
                      </Tippy>
                    </HeadlessUiPopover>
                  </div>
                </>
              </HasAccess>

              <span className="font-medium">Tech Fee Rate</span>
              <div className="flex w-full items-center justify-between gap-y-2">
                <div
                  ref={setTechFeeRateSwitchEl}
                  onMouseEnter={() => setShowTechFeeRateToggle(true)}
                  onMouseLeave={() => setShowTechFeeRateToggle(false)}
                >
                  <Switch
                    checked={Boolean(editedMerchantConfigData?.useTechFee)}
                    onChange={() => {
                      setUsingTechFee((p) => !p);
                      handleEditableFieldChange("useTechFee", !usingTechFee);
                    }}
                    disabled={techFeeRateSwitchDisabled}
                    label=""
                    labelProps={{ className: "font-medium w-100 mr-0" }}
                  />
                  <Popover
                    open={showTechFeeRateToggle && useInterchangeRate}
                    placement="bottom"
                    referenceEl={techFeeRateSwitchEl}
                  >
                    <div className="rounded-lg bg-black p-2 text-white shadow-lg">
                      {`Account Holder ${accountHolderCode} has Interchange++ on and Tech Fees does not apply.`}
                    </div>
                  </Popover>
                </div>

                <Input
                  id="techFeeRate"
                  type="number"
                  label="Percentage"
                  containerProps={{
                    className: "w-[85px]",
                  }}
                  value={editedMerchantConfigData?.techFeeRate ?? ""}
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  onChange={(e) => {
                    if (e.target.value && parseFloat(e.target.value) <= 0) {
                      openSnackbar("Tech Fee Rate must be > 0", {
                        type: "error",
                        duration: 3000,
                      });
                    } else if (
                      e.target.value &&
                      parseFloat(e.target.value) > 100
                    ) {
                      openSnackbar("Tech Fee Rate cannot be greater than 100", {
                        type: "error",
                        duration: 4000,
                      });
                    } else {
                      handleEditableFieldChange("techFeeRate", e.target.value);
                    }
                  }}
                  disabled={!isEditing}
                />
              </div>

              <span className="font-medium">Surcharge Rate</span>
              <div className="flex w-full items-center justify-between gap-y-2">
                <div
                  ref={setSurchargeRateSwitchEl}
                  onMouseEnter={() => setShowSurchargeRateToggle(true)}
                  onMouseLeave={() => setShowSurchargeRateToggle(false)}
                >
                  <Switch
                    checked={Boolean(editedMerchantConfigData?.useConvFee)}
                    onChange={() => {
                      setUsingConvFee((p) => !p);
                      handleEditableFieldChange("useConvFee", !usingConvFee);
                    }}
                    disabled={convFeeSwitchDisabled}
                    label=""
                    labelProps={{ className: "font-medium w-100 mr-0" }}
                  />
                  {process.env.REACT_APP_FF_DISPLAY_ICPP_SURCHARGE?.toLocaleLowerCase() !==
                    "true" && (
                    <Popover
                      open={showSurchargeRateToggle && useInterchangeRate}
                      placement="bottom"
                      referenceEl={surchargeRateSwitchEl}
                    >
                      <div className="rounded-lg bg-black p-2 text-white shadow-lg">
                        {`Account Holder ${accountHolderCode} has Interchange++ on and Surcharge does not apply.`}
                      </div>
                    </Popover>
                  )}
                </div>
                <div className="col-span-2 ml-7 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="col-span-1 col-start-2">
                      <Input
                        id="rateFixedValue"
                        type="number"
                        label="Percentage"
                        value={editedMerchantConfigData?.convFeeRate ?? ""}
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        onChange={(e) => {
                          if (
                            e.target.value &&
                            parseFloat(e.target.value) <= 0
                          ) {
                            openSnackbar(
                              "Surcharge Fixed Rate must be greater than 0",
                              {
                                type: "error",
                                duration: 4000,
                              }
                            );
                          } else if (
                            e.target.value &&
                            parseFloat(e.target.value) > 4
                          ) {
                            openSnackbar(
                              "Surcharge Fixed Rate cannot be greater than 4",
                              {
                                type: "error",
                                duration: 4000,
                              }
                            );
                          } else {
                            handleEditableFieldChange(
                              "convFeeRate",
                              e.target.value
                            );
                          }
                        }}
                        disabled={
                          !isEditing ||
                          (useInterchangeRate &&
                            process.env.REACT_APP_FF_DISPLAY_ICPP_SURCHARGE?.toLocaleLowerCase() !==
                              "true")
                        }
                        containerProps={{
                          className: "w-[85px] ml-12",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Fee Rate section (feature-flagged) */}
              {showCustomFeeSection && (
                <>
                  <span className="font-medium">Custom Fee Rate</span>
                  {/* Row 1: switch + Percentage */}
                  <div className="flex w-full items-center justify-between gap-y-2">
                    <Switch
                      checked={editedMerchantConfigData?.useCorpFee ?? false}
                      onChange={() => {
                        const next = !usingCorpFee;
                        setUsingCorpFee(next);
                        handleEditableFieldChange("useCorpFee", next);
                      }}
                      disabled={!isEditing}
                      label=""
                      labelProps={{ className: "font-medium w-100 mr-0" }}
                    />

                    <Input
                      id="corpFee"
                      type="number"
                      label="Percentage"
                      containerProps={{ className: "w-[85px]" }}
                      value={editedMerchantConfigData?.corpFee ?? ""}
                      onWheel={(e) => (e.target as HTMLElement).blur()}
                      onChange={(e) => {
                        if (e.target.value && parseFloat(e.target.value) <= 0) {
                          openSnackbar("Custom Fee Rate must be > 0", {
                            type: "error",
                            duration: 3000,
                          });
                        } else if (
                          e.target.value &&
                          parseFloat(e.target.value) > 100
                        ) {
                          openSnackbar(
                            "Custom Fee Rate cannot be greater than 100",
                            {
                              type: "error",
                              duration: 4000,
                            }
                          );
                        } else {
                          handleEditableFieldChange("corpFee", e.target.value);
                        }
                      }}
                      disabled={
                        !isEditing ||
                        !Boolean(editedMerchantConfigData?.useCorpFee)
                      }
                    />
                  </div>

                  {/* Row 2: Description + Corp ID (indented) */}
                  <div className="col-span-2 ml-4 flex items-end justify-end gap-x-3">
                    <Input
                      id="corpFeeDescription"
                      label="Description"
                      value={editedMerchantConfigData?.corpFeeDescription ?? ""}
                      onChange={(e) =>
                        handleEditableFieldChange(
                          "corpFeeDescription",
                          e.target.value
                        )
                      }
                      disabled={
                        !isEditing ||
                        !Boolean(editedMerchantConfigData?.useCorpFee)
                      }
                      labelProps={{ className: "" }}
                      containerProps={{ className: "w-[200px]" }}
                    />
                    <div className="flex w-[120px] flex-col">
                      <label className="mb-1 font-medium">Corp ID</label>
                      <Select
                        id="corpAccountHolderId"
                        value={
                          editedMerchantConfigData?.corpAccountHolderId ?? ""
                        }
                        onChange={(e) =>
                          handleEditableFieldChange(
                            "corpAccountHolderId",
                            e.target.value
                          )
                        }
                        disabled={
                          !isEditing ||
                          !Boolean(editedMerchantConfigData?.useCorpFee) ||
                          isLoadingCorporations
                        }
                        className="w-full"
                        options={corpOptions}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          <HasAccess allowedRoles={[...csrRoles]}>
            <span className="font-medium">Timezone</span>
            <Select
              id="timezone"
              value={editedMerchantConfigData?.timeZoneCode}
              options={
                timezones?.map((x) => ({
                  value: x.code,
                  label: x.name,
                })) ?? []
              }
              onChange={(e) =>
                handleEditableFieldChange("timeZoneCode", e.target.value)
              }
              loading={isLoadingTimezones}
              disabled={!isEditing}
              className="w-full"
            />
            {!isCorpEdition && (
              <>
                <span className="pt-1 font-medium">Duplicate Detection</span>
                <div className="flex w-full items-center justify-between gap-x-2">
                  <Switch
                    checked={Boolean(usingDuplicateDetectionMinutes)}
                    onChange={() =>
                      handleUseDuplicationDetectionMinutesChange()
                    }
                    disabled={!isEditing}
                    label=""
                    labelProps={{ className: "font-medium w-100 mr-0" }}
                  />
                  <HeadlessUiPopover className="relative">
                    <Tippy
                      className="mb-3"
                      placement="right"
                      content="Zero means no duplication detection"
                    >
                      <Input
                        ref={duplicateDetectionMinutesRef}
                        id="duplicateDetectionMinutes"
                        type="string"
                        label="Minutes"
                        containerProps={{
                          className: "w-[85px]",
                        }}
                        disabled={!isEditing || !usingDuplicateDetectionMinutes}
                        value={
                          editedMerchantConfigData?.duplicateDetectionMinutes ??
                          ""
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          const num = Number(v);

                          // Allow clearing the field without showing errors
                          if (v === "") {
                            handleEditableFieldChange(
                              "duplicateDetectionMinutes",
                              e.target.value
                            );
                            return;
                          }

                          // Show validation errors for invalid values
                          if (!Number.isInteger(num)) {
                            openSnackbar(
                              "Duplicate detection minutes must be a whole number",
                              {
                                type: "error",
                                duration: 3000,
                              }
                            );
                          } else if (num < 0) {
                            openSnackbar(
                              "Duplicate detection minutes must be >= 0",
                              {
                                type: "error",
                                duration: 3000,
                              }
                            );
                          } else if (num > 1440) {
                            openSnackbar(
                              "Duplicate detection minutes must be <= 1440",
                              {
                                type: "error",
                                duration: 3000,
                              }
                            );
                          } else {
                            handleEditableFieldChange(
                              "duplicateDetectionMinutes",
                              e.target.value
                            );
                          }
                        }}
                      />
                    </Tippy>
                  </HeadlessUiPopover>
                </div>
              </>
            )}
          </HasAccess>
        </div>
      </Panel.Content>
    </Panel>
  );
}

export default MerchantConfigurations;
